import type {AriaProps} from './aria/interface';

export type ShellLocation = {
  pathname: string;
  search: string;
  hash: string;
  state?: unknown;
};

/** A shell origin the shell serves: each one is its own URL namespace. */
export type ShellSurface = 'forge' | 'identity';

/**
 * A shell path a remote renders, paired with the path inside the remote's own
 * router that serves it. A route claim belongs to exactly one surface, since a path
 * only identifies a page together with the origin it is served from.
 *
 * A `shellPath` is a single top-level segment; `remotePath` is any absolute
 * path in the remote's router, `/` included. Both directions resolve by longest
 * prefix, so a route claim covers everything under its paths.
 *
 * @example A mount: the remote's root, served at `/wandb`.
 * `{surface: 'forge', shellPath: '/wandb', remotePath: '/'}`
 * `forge.coreweave.com/wandb/my-team/my-project` renders remote
 * `/my-team/my-project`.
 *
 * @example An extra route claim: a page the remote serves outside its mount.
 * `{surface: 'forge', shellPath: '/notebooks', remotePath: '/notebooks'}`
 * `forge.coreweave.com/notebooks` renders remote `/notebooks`, and
 * `/wandb/notebooks` redirects to it, because a remote path has one canonical
 * shell URL.
 *
 * @example A route claim on a different surface, ignored while resolving Forge.
 * `{surface: 'identity', shellPath: '/profile', remotePath: '/profile'}`
 *
 * A shell path belongs to at most one remote: if two remotes claim the same
 * one, the shell honors neither and serves its own not-found there, so a new
 * claim can take down a path another remote already serves. Check what claims
 * the path today, across every remote's manifest, before adding one.
 */
export type RouteClaim = {
  surface: ShellSurface;
  shellPath: string;
  remotePath: string;
};

export type RemoteConfig = {
  /**
   * Schema a remote publishes for shell discovery and the validated shape the
   * shell loads. Unknown fields are ignored. Missing or non-v1 versions are
   * rejected.
   */
  version: 'v1';
  /** JavaScript file URL for the federation entry, imported as a module. */
  entry: string;
  /**
   * JavaScript file URL for the config script, loaded as a classic script
   * before the entry module.
   */
  configScriptUrl?: string;
  /**
   * URL prefix the remote's non-JavaScript files are served from: images,
   * stylesheets, fonts, documents loaded in an iframe. Module federation
   * resolves the remote's own modules and everything a bundler rewrites, so
   * this covers what is left — a path the remote's code builds by hand.
   *
   * Needed because the shell owns the root of its origin: a remote's `/logo.png`
   * resolves against the shell, which serves its own document there. Either an
   * absolute URL or a path on the shell origin the shell routes to the remote's
   * assets. Omitted by a remote whose files a bundler resolves.
   */
  assetBase?: string;
  /**
   * Every top-level shell path this remote renders, its mount included. Route
   * claims are validated one at a time and rejected ones are dropped, so an
   * unusable extra claim leaves the mount reachable.
   */
  routes?: RouteClaim[];
};

export type ShellRouter = {
  /** Shell path of the remote's mount, i.e. the shell path of remote `/`. */
  basePath: string;
  /**
   * Stable product identity serving the current page (e.g. `wandb`,
   * `registry`, `notebooks`). The shell owns the URL-to-product mapping, so
   * this stays fixed even if the shell later changes the URL that serves a
   * product. Absent when the current page is shell-owned rather than served by
   * a product.
   */
  activeProductId?: string;
  /** Every routeClaim the shell resolved for this remote, including the mount. */
  routeClaims: RouteClaim[];
  location: ShellLocation;
  /**
   * Canonical shell URL for a path inside the remote's router, by longest
   * claimed remote path. Optional because the contract is copied into the
   * other product repositories; remotes built against a shell that predates it
   * fall back to prefixing `basePath`.
   */
  createHref?(remotePath: string): string;
  navigate(
    to: string,
    options?: {
      replace?: boolean;
      state?: unknown;
    }
  ): void;
  go(delta: number): void;
};

/**
 * Properties on an analytics event. Not `Record<string, any>`, which lets an
 * array through — Segment accepts a list as `properties` but will not process
 * it.
 */
export type AnalyticsProperties = Record<string, unknown>;

/**
 * Per-call delivery options, passed through to the analytics vendor untouched —
 * `context` overrides, per-integration toggles. Distinct from properties, which
 * describe the event; these describe how to send it.
 *
 * Open-ended, with the one field anyone actually uses written down. The
 * intersection with an index signature is what keeps it open: an unlisted
 * option still type-checks, so this documents without restricting.
 */
export type AnalyticsOptions = {
  /**
   * Vendor context overrides for this call. The shell merges its own values in
   * — `hostSessionID` on `page` — so a caller's `context` is added to, never
   * replaced.
   */
  context?: AnalyticsProperties;
} & Record<string, unknown>;

/**
 * Fields the route-change `page` events agree on, across the shell and every
 * remote. All optional: a page event with none of them is still a valid page
 * event.
 */
type RoutePageProperties = {
  /**
   * The route's view name — a stable identifier for the page, not its title.
   * `shell-home` in the shell; the route's `datadogViewName` in the wandb
   * remote.
   */
  routeName?: string;
  /** Sub-view within a route, for a route that has tabs. */
  tab?: string;
  /**
   * `routeName` and `tab` joined with a colon, for reports that group on a
   * single field. A clunky name, kept because existing reports key on it.
   */
  routeAndTab?: string;
};

/**
 * Properties on a `page` event: the shared route fields, plus anything else the
 * caller wants to send.
 */
export type PageProperties = RoutePageProperties & {
  /**
   * A property that happens to be called `name` — *not* Segment's page name,
   * which is a separate argument to `analytics.page()` that this contract does
   * not expose. `forceRedirect` in the wandb remote sends
   * `{name: 'External Redirect'}`, and it arrives as an ordinary property.
   */
  name?: string;
} & AnalyticsProperties;

/**
 * A remote's handle on user tracking. The shell owns loading the analytics
 * vendor, cookie consent, and viewer identity, so a remote only ever describes
 * what happened.
 *
 * Deliberately no `identify`, `group`, `reset` or `alias`: identity is
 * shell-owned, and a remote that could reassign the user id would clobber every
 * other remote's attribution.
 */
export type ShellAnalytics = {
  /*
    Every call is stamped with the remote's name as `app_name`, overriding any
    `app_name` in the caller's properties — it is an attribution field a remote
    cannot spoof. The name itself is not exposed: a remote already knows what it
    is, and the shell tracks which remote is mounted on its own.
  */
  track(event: string, properties?: AnalyticsProperties): void;
  /**
   * An arrival on a page, once per page the visitor lands on. Anything else is
   * a `track` event.
   *
   * The vendor fills in the url, path, referrer and title itself, so send only
   * the route fields. The shell adds `app_name` and the host session id.
   */
  page(properties?: PageProperties, options?: AnalyticsOptions): void;
  /**
   * A child handle that merges `properties` into every call made through it.
   * Callers' own properties win over the scope's; `app_name` wins over both.
   */
  scoped(properties: AnalyticsProperties): ShellAnalytics;
};

/**
 * What a remote is handed at mount: its analytics, plus one opt-in escape
 * hatch. Wider than `ShellAnalytics` so that `scoped()` — which returns the
 * narrow type — does not carry the hatch onto every child scope.
 */
export type RemoteAnalytics = ShellAnalytics & {
  /**
   * Replaces `window.analytics` with a proxy that stamps this remote's
   * `app_name` onto `track` and `page` and forwards everything else to the real
   * Segment instance.
   *
   * For a remote that still reaches for the Segment global directly and cannot
   * migrate those call sites yet. Nothing else needs it, and a remote that does
   * not call it gets an untouched global.
   *
   * The shell removes the proxy when the remote unmounts, so there is nothing
   * to dispose of here.
   */
  installSegmentGlobalProxy?(): void;
};

export type RemoteErrorKind = 'unauthenticated' | 'unknown';

export type RemoteError = {
  kind: RemoteErrorKind;
  message?: string;
};

/** The account the shell has selected; enough for a remote to match against
 * its own account list. `null` when no user is signed in or the account list
 * is empty. */
export type ShellAccount = {
  id: string;
  name: string;
  kind: 'organization' | 'personal';
};

/**
 * Header controls a remote contributes to the shell's header, published via
 * `patchHeader({actions})`. The shell renders each control only while its
 * callback is present, so a remote without global search leaves
 * `onGlobalSearchPress` out and gets no button.
 */
export type ShellHeaderActions = {
  /** Opens the remote's global search. */
  onGlobalSearchPress?(): void;
  /**
   * Opens the remote's keyboard shortcuts reference, listed in the shell's
   * help menu. The shortcuts themselves are the remote's, so the shell never
   * lists them on its own.
   */
  onKeyboardShortcutsPress?(): void;
};

export type RemoteMountProps = {
  router: ShellRouter;
  /**
   * The account selected by the shell. `undefined` means the shell has not
   * resolved its account data yet (the resolved value arrives via
   * `RemoteMount.update()`); `null` means resolved but signed out or no
   * accounts; an object is the selected account.
   */
  selectedAccount: ShellAccount | null | undefined;
  /**
   * The remote-to-shell error channel. The remote owns a separate React root,
   * so the shell's error boundary cannot see inside it. The remote wraps
   * whatever it wants to report, such as a crash caught by its own boundary or
   * a deliberate signal such as `unauthenticated`, into a RemoteError.
   * `unauthenticated` means this remote needs a logged-in viewer, so the shell
   * should take the user to login. `unknown` is everything else.
   * Human-readable detail goes in `message`. This is optional when a remote
   * handles its own fatal UI.
   */
  onError?(error: RemoteError): void;
  /**
   * User tracking for this remote, with the shell's setup already done.
   * Optional for the same reason `ShellRouter.createHref` is: the contract is
   * copied into the other product repositories, so a remote can be built
   * against a newer contract than the deployed shell. Treat an absent handle as
   * "tracking is unavailable", not an error.
   */
  analytics?: RemoteAnalytics;
  /**
   * The remote's own `RemoteConfig.assetBase`, handed back so its code can
   * resolve its non-JavaScript files without knowing where the shell deployed
   * it. Absent when the remote publishes no asset base, and optional for the
   * same reason `analytics` is.
   */
  assetBase?: string;
  /**
   * Applies a partial update to shell header state. Each field present in the
   * patch replaces the shell's current value for it; absent fields are left
   * alone. The shell drops everything the remote published when it unmounts.
   * Optional for the same reason `analytics` is.
   */
  patchHeader?: (patch: ShellHeaderPatch) => void;
  /**
   * Whether the ARIA header mount should render the W&B toast queue. Product
   * remotes ignore this optional mount property.
   */
  showToastRegion?: boolean;
  /**
   * The shell's shared Aria integration: controller access and registration.
   * Optional because older shells do not provide it. Changes arrive through
   * `MountedRemote.update()`, including controller availability changes.
   */
  aria?: AriaProps;
};

export type ShellHeaderPatch = {
  breadcrumbs?: readonly ShellHeaderBreadcrumb[];
  /**
   * The header controls this remote currently offers. Publish at mount and
   * again whenever one appears or goes away, e.g. a feature behind a flag
   * finishes mounting.
   */
  actions?: ShellHeaderActions;
};

export type ShellHeaderBreadcrumb = {
  label: string;
  href?: string;
};

export type MountedRemote = {
  update(props: RemoteMountProps): void;
  unmount(): void;
};

export type RemoteMount = (
  container: HTMLElement,
  props: RemoteMountProps
) => MountedRemote;

// --- ARIA -------------------------------------------------------------------
//
// ARIA is not a configured remote. The wandb remote's federation container
// exposes a second module for it, which the shell mounts into its header on
// every page. This section is the contract for that second mount.

/** Module the wandb remote exposes for the ARIA mount. */
export const ARIA_MODULE_ID = './mount-aria';

/**
 * CSS custom property the mounted ARIA drawer sets on `<html>` while it is
 * open: the width, in px, that page content should leave free on the right.
 * The shell and every remote apply it as a right margin on their main area.
 */
export const ARIA_DRAWER_WIDTH_CSS_VAR = '--wb-agent-drawer-width';

/**
 * CSS custom property the shell sets to the height of its header, so the
 * ARIA drawer knows where to start when mounted under the shell.
 */
export const ARIA_DRAWER_TOP_CSS_VAR = '--wb-agent-drawer-top';
